/**
 * What the tutorial teaches, in order — as data.
 *
 * The tutorial's decisions live here for the same reason `micCheckWindow.ts` and
 * `latency.ts` hold the setup gates': a component that owns both the
 * decision and the presentation cannot be tested without a browser, and the
 * decision is the half worth testing. `Tutorial.tsx` draws these; nothing in
 * this file touches a canvas, a DOM node or React.
 *
 * Derived, not tabulated. The steps are built from `/api/config` and from the
 * map actually being played, so a kitchen with no oven never explains one and a
 * fifteenth ingredient added to `recipes.json` is taught with no edit here. The
 * exemplar the middle steps follow — take *this*, chop *this*, cook it in
 * *this* — is the first item of the first dish this map can order, so the
 * tutorial teaches with food the player is about to be asked for.
 *
 * `client/test/tutorialSteps.test.ts` asserts all of that against the shipped
 * content, including that every station the played map's dishes need is taught
 * by some step.
 */

import type {
  Carryable,
  Cell,
  ClientConfig,
  ContainerKind,
  CookState,
  DishSummary,
  IngredientId,
  IngredientSummary,
  MapSummary,
  RequiredItem,
  StationType,
} from '@overcooked/shared'
import { cellKey, isCookingEquipment, msToTicks } from '@overcooked/shared'
import type { GameState } from '@overcooked/engine'
import { cloneState } from '@overcooked/engine'
import type { TutorialGoal, TutorialSetup } from './tutorialSandbox'
import { HIDEABLE_STATIONS, SANDBOX_PLAYER_ID } from './tutorialSandbox'

/**
 * One picture a step asks for.
 *
 * Six kinds, because the tutorial draws with the kitchen's own functions and
 * those functions take six different sorts of subject. A `station` spec is a
 * whole tile — `Tutorial.tsx` hands it to `drawStation`, the same call the
 * kitchen canvas makes — so a hob can be shown with a pan on it mid-cook, or
 * alight, which is the state prose is worst at describing.
 */
export type DrawSpec =
  | { kind: 'ingredient'; id: IngredientId }
  | {
      kind: 'station'
      station: StationType
      /** Crates only: what this one dispenses. */
      dispenses?: IngredientId
      /** Cooking equipment only: the vessel standing on it, and how it is doing. */
      container?: ContainerKind
      contents?: IngredientId[]
      cookState?: CookState
      onFire?: boolean
    }
  | { kind: 'container'; container: ContainerKind; contents?: IngredientId[]; cookState?: CookState }
  | { kind: 'plate'; dirty?: boolean; contents?: IngredientId[] }
  | { kind: 'equipment'; id: 'extinguisher' }
  | { kind: 'fire' }

/**
 * What stays lit while this step is being taught.
 *
 * A region is named by *station type* rather than by cell, because the steps are
 * derived from a `ClientConfig` and a map name — there is no grid in front of
 * this module to take coordinates from. `focusCells` resolves a spec against the
 * kitchen the player is actually standing in, which is the only place both
 * halves are known.
 */
export type FocusSpec =
  /** Nothing is dimmed: the lesson is the room, or moving around in it. */
  | { kind: 'kitchen' }
  /** Every tile of these types stays lit; everything else is dimmed. */
  | { kind: 'stations'; stations: StationType[] }

/**
 * The tiles a step's focus names, in reading order.
 *
 * Empty means "dim nothing" — both for `kitchen` and for a spec naming stations
 * this kitchen does not have, because a dim with no hole in it is a screen that
 * has hidden the lesson rather than pointed at it.
 */
export function focusCells(focus: FocusSpec, state: GameState): Cell[] {
  if (focus.kind === 'kitchen') return []
  const wanted = new Set<StationType>(focus.stations)
  return Object.values(state.stations)
    .filter((station) => wanted.has(station.type))
    .map((station) => ({ ...station.cell }))
    .sort((a, b) => a.y - b.y || a.x - b.x)
}

export interface TutorialStep {
  id: string
  title: string
  body: string
  /**
   * The one sentence that says what to do, as an imperative naming the key.
   *
   * The body explains the rule; this says which finger moves. They are separate
   * fields because a player who has read the rule once still wants the
   * instruction on screen, and `tutorialSteps.test.ts` asserts every step has
   * one — which is where "explicit instructions on what to press" is enforced
   * rather than hoped for.
   */
  instruction: string
  /** Drawn as `<kbd>`, in the order they are pressed. Never empty. */
  keys: string[]
  /** What stays lit while this step is up. */
  focus: FocusSpec
  /**
   * Fixtures that come into the practice kitchen at this step and are absent
   * before it. `tutorialSandbox.ts`'s `stage` is what applies them, from the
   * whole prefix of steps, so a player who arrives at a step directly sees
   * exactly what walking there would have shown them.
   */
  reveals?: StationType[]
  /** Ingredients whose crates become available from this step onward. */
  unlocks?: IngredientId[]
  /** Start this capstone in a clean kitchen rather than carrying the prior lesson forward. */
  fresh?: boolean
  draw: DrawSpec[]
  /**
   * Drives the rAF loop. A step that has nothing moving in it never ticks, so
   * the gate is a still picture on nine screens out of ten and a moving one
   * exactly where the motion *is* the lesson — a flame, a burner, the water in
   * the sink, a bar filling toward its notch.
   */
  animated: boolean
  /**
   * Set on the last step only: the dish whose real route strip is shown, drawn
   * with the same `RouteIcon` the docket uses. Teaching the ticket with a
   * picture of a ticket is the point.
   */
  dish?: string
  /**
   * What the player has to *do* for this step to be over.
   *
   * A predicate over the engine's own events, so a step ends because the
   * kitchen said so and not because a button was pressed. A step without one is
   * a reading card — the last step is a picture of a ticket, and there is
   * nothing to perform on it.
   */
  goal?: TutorialGoal
  /** Keep this completed state visible until the learner explicitly continues. */
  pauseAfterGoal?: boolean
  /**
   * The kitchen this step needs, staged before it starts.
   *
   * Waiting for a pan to burn and then catch fire is four seconds of nothing
   * followed by a lesson; the fire step lights the stove instead. Setups exist
   * for exactly that reason — to put the player at the start of the thing being
   * taught rather than at the start of the story that leads to it.
   */
  setup?: TutorialSetup
}

// ---------------------------------------------------------------------------
// What each step is waiting for
//
// Verbs, not ingredients. `stepsFor` derives its prose and its pictures from
// whichever kitchen it was handed; these predicates hold for any of them,
// because "the player chopped something" is the same event whatever was on the
// board.
// ---------------------------------------------------------------------------

const walked: TutorialGoal = (event) => event.type === 'PLAYER_MOVED'
/**
 * The first interaction is deliberately a complete move: take the pan from
 * the counter *and* put it on the heat. A loose `PICKED_UP` goal used to let a
 * player finish the lesson by lifting the pan, then set it back on any counter
 * while looking for the crate with full hands.
 */
const putPanOnHeat: TutorialGoal = (event, state) => {
  if (event.type !== 'PUT_DOWN' || event.item.type !== 'container') return false
  const station = state.stations[cellKey(event.cell)]
  return station !== undefined && isCookingEquipment(station.type)
}
const tookAnIngredient: TutorialGoal = (event) =>
  event.type === 'PICKED_UP' && event.item.type === 'ingredient'
const choppedSomething: TutorialGoal = (event) => event.type === 'CHOPPED'
const startedCooking: TutorialGoal = (event) =>
  event.type === 'COOK_STATE_CHANGED' && event.state === 'cooking'
const cookedThrough: TutorialGoal = (event) =>
  event.type === 'COOK_STATE_CHANGED' && event.state === 'done'
/** Food reaching the bin, which is the only thing the bin takes. A plate or the
 *  extinguisher pressed here is refused and told where it actually goes, so
 *  neither can finish this step by accident. */
const threwSomethingAway: TutorialGoal = (event) => event.type === 'TRASHED'
/** The fire step is not over when the flame is. Putting it out leaves the
 *  extinguisher in your hands, and the lesson is the walk back to the rack —
 *  the thing a team that skips it pays for at the next fire. */
const rackedTheExtinguisher: TutorialGoal = (event, state) =>
  event.type === 'PUT_DOWN' && state.stations[cellKey(event.cell)]?.type === 'extinguisher_rack'
const platedUp: TutorialGoal = (event) => event.type === 'PLATED'
/** The one-ingredient pan's whole consequence: two cooks meeting on one plate. */
const assembledADish: TutorialGoal = (event) => event.type === 'PLATED' && event.contents.length >= 2
const served: TutorialGoal = (event) => event.type === 'SERVED'
/** The capstone is complete only when the complete ticket, not a partial plate,
 *  reaches the hatch. */
const servedDishGoals = new Map<string, TutorialGoal>()
function servedDish(contents: IngredientId[]): TutorialGoal {
  // `stepsFor` is deliberately deterministic: the same kitchen must yield the
  // same list even when it is rebuilt for a returning reader. Keep the closure
  // that remembers this recipe stable as well as keeping its answer stable.
  const key = [...contents].sort().join('|')
  const known = servedDishGoals.get(key)
  if (known) return known
  const goal: TutorialGoal = (event) =>
    event.type === 'SERVED' &&
    event.contents.length === contents.length &&
    event.contents.every((item) => contents.includes(item))
  servedDishGoals.set(key, goal)
  return goal
}
const washed: TutorialGoal = (event) => event.type === 'WASHED'
/** A clean plate going home, which is the half of the bin lesson the bin cannot
 *  teach: the stack takes one back, so full counters are never a dead end. */
const stackedAPlate: TutorialGoal = (event, state) =>
  event.type === 'PUT_DOWN' && state.stations[cellKey(event.cell)]?.type === 'plate_stack'

// ---------------------------------------------------------------------------
// Staging the sandbox kitchen
//
// Every setup is pure: it clones and returns, never mutates what it was given,
// so a player pressing Back twice gets the same kitchen both times.
// ---------------------------------------------------------------------------

function stationOfType(state: GameState, type: StationType) {
  return Object.values(state.stations).find((station) => station.type === type)
}

/** Hands empty, and nothing half-held anywhere. Every setup wants both. */
function readyToStart(state: GameState): GameState {
  const next = cloneState(state)
  const chef = next.players[SANDBOX_PLAYER_ID]
  if (chef) chef.holding = null
  for (const station of Object.values(next.stations)) {
    station.heldBy = null
    station.resumedAtTick = null
  }
  return next
}

interface ProgressionSetups {
  panOnHeat: TutorialSetup
  sourceInHand: TutorialSetup
  choppedOnBoard: TutorialSetup
  cookingInPan: TutorialSetup
  burntPanOnHeat: TutorialSetup
}

// `stepsFor` is deterministic by contract. These closures remember a kitchen's
// exemplar, so retain them by that exemplar instead of making new functions on
// every call (the steps themselves are compared structurally in the tests).
const progressionSetups = new Map<string, ProgressionSetups>()
function progressionSetupsFor(
  source: IngredientId | null,
  chopped: IngredientId | null,
  cookingStation: StationType,
): ProgressionSetups {
  const key = `${source ?? ''}|${chopped ?? ''}|${cookingStation}`
  const known = progressionSetups.get(key)
  if (known) return known

  /** Put the starting pan where the story has already brought it: on the heat. */
  const panOnHeat: TutorialSetup = (state) => {
    const next = readyToStart(state)
    const stove = stationOfType(next, cookingStation)
    const panStation = Object.values(next.stations).find((station) => station.item?.type === 'container')
    if (!stove || !panStation || panStation.item?.type !== 'container') return next
    const pan = panStation.item.container
    panStation.item = null
    stove.container = { ...pan, contents: [], cookState: 'empty', phaseStartedAtTick: null, phaseEndsAtTick: null }
    return next
  }
  /** Each direct jump begins at the exact handoff the preceding card creates. */
  const sourceInHand: TutorialSetup = (state) => {
    const next = panOnHeat(state)
    const chef = next.players[SANDBOX_PLAYER_ID]
    if (chef && source) chef.holding = { type: 'ingredient', id: source }
    return next
  }
  const choppedOnBoard: TutorialSetup = (state) => {
    const next = panOnHeat(state)
    const board = stationOfType(next, 'board')
    if (board && chopped) board.item = { type: 'ingredient', id: chopped }
    return next
  }
  const cookingInPan: TutorialSetup = (state) => {
    const next = panOnHeat(state)
    const stove = stationOfType(next, cookingStation)
    if (stove?.container && chopped) {
      stove.container = {
        ...stove.container,
        contents: [chopped],
        cookState: 'cooking',
        phaseStartedAtTick: next.tick,
        phaseEndsAtTick: next.tick + msToTicks(4_000),
      }
    }
    return next
  }
  const burntPanOnHeat: TutorialSetup = (state) => {
    const next = cookingInPan(state)
    const stove = stationOfType(next, cookingStation)
    if (stove?.container) {
      stove.container = {
        ...stove.container,
        cookState: 'burnt',
        phaseStartedAtTick: null,
        phaseEndsAtTick: null,
      }
    }
    return next
  }
  const setups = { panOnHeat, sourceInHand, choppedOnBoard, cookingInPan, burntPanOnHeat }
  progressionSetups.set(key, setups)
  return setups
}

/** A stove already alight, because the four seconds before it are not a lesson. */
const lightTheStove: TutorialSetup = (state) => {
  const next = readyToStart(state)
  const stove = stationOfType(next, 'stove')
  if (stove) {
    stove.onFire = true
    stove.progressTicks = 0
    stove.progressTargetTicks = 0
  }
  const rack = stationOfType(next, 'extinguisher_rack')
  if (rack && !rack.item) rack.item = { type: 'equipment', id: 'extinguisher' }
  return next
}

/**
 * What the two plating steps put in the pan, in the order they put it there.
 *
 * The sandbox kitchen's lead dish is two cooked items, and these are they.
 * Named here rather than derived because a setup runs against a `GameState` and
 * has no `ClientConfig` to ask — and because the sandbox map ships with the
 * recipe, so the two cannot drift without `tutorialSandbox.test.ts` noticing.
 */
const COOKED_FOR_PLATING = 'fried_meat'
const COOKED_FOR_ASSEMBLY = 'stewed_carrot'

/** Cooked and waiting on the stove, with a burn clock still running under it. */
function cookedInThePan(next: GameState, contents: IngredientId[]): void {
  const stove = stationOfType(next, 'stove')
  if (!stove) return
  stove.onFire = false
  stove.container = {
    id: 'tutorial-pan',
    kind: 'pan',
    contents: [...contents],
    cookState: 'done',
    phaseStartedAtTick: next.tick,
    // A generous window rather than an endless one: it is still the burn
    // clock, so somebody who wanders off learns the right lesson.
    phaseEndsAtTick: next.tick + msToTicks(120_000),
  }
}

/**
 * A clean plate in the chef's hands, carrying `contents`.
 *
 * It is the plate they are already holding where there is one, and comes off
 * the stack otherwise, because the kitchen owns exactly `platesPerMap` of them
 * and a setup that minted a fourth would be practising a rule the round does
 * not have. If the hands are empty and the stack is too it mints one anyway: a
 * staged step nobody can perform is a worse lie than a plate that appeared.
 */
function plateInHand(next: GameState, was: Carryable | null, contents: IngredientId[]): void {
  const chef = next.players[SANDBOX_PLAYER_ID]
  if (!chef) return
  let id = was?.type === 'plate' ? was.plate.id : null
  if (id === null) {
    const stack = stationOfType(next, 'plate_stack')
    if (stack && stack.cleanPlates > 0) {
      // The engine's own id scheme, so a plate staged here and a plate taken by
      // hand are the same kind of thing to everything downstream.
      id = `plate-${stack.cell.x}-${stack.cell.y}-${stack.cleanPlates}`
      stack.cleanPlates -= 1
    }
  }
  chef.holding = {
    type: 'plate',
    plate: { id: id ?? 'tutorial-plate', clean: true, contents: [...contents] },
  }
}

/** Food cooked and waiting: this step is about the plate, not about the pan. */
const foodReadyToPlate: TutorialSetup = (state) => {
  const next = readyToStart(state)
  cookedInThePan(next, [COOKED_FOR_PLATING])
  const stack = stationOfType(next, 'plate_stack')
  if (stack && stack.cleanPlates < 1) stack.cleanPlates = 1
  return next
}

/**
 * Half a dish in the hands and the other half on the heat.
 *
 * The pan holds one thing, so the second cook has already happened by the time
 * this card is up — waiting through it again would be teaching patience rather
 * than assembly. What is left is the press that matters: the plate goes to the
 * pan, and the dish is built where the food lands.
 */
const halfADish: TutorialSetup = (state) => {
  const held = state.players[SANDBOX_PLAYER_ID]?.holding ?? null
  const next = readyToStart(state)
  cookedInThePan(next, [COOKED_FOR_ASSEMBLY])
  plateInHand(next, held, [COOKED_FOR_PLATING])
  return next
}

/** The finished dish, in hand, with only the walk to the hatch left. */
const dishReadyToServe: TutorialSetup = (state) => {
  const held = state.players[SANDBOX_PLAYER_ID]?.holding ?? null
  const next = readyToStart(state)
  plateInHand(next, held, [COOKED_FOR_PLATING, COOKED_FOR_ASSEMBLY])
  return next
}

/** A dirty plate on the return, which is where one always turns up. */
const dirtyPlateWaiting: TutorialSetup = (state) => {
  const next = readyToStart(state)
  const ret = stationOfType(next, 'plate_return')
  if (ret && ret.dirtyPlates < 1) ret.dirtyPlates = 1
  return next
}

/** A clean, empty plate and nowhere obvious to put it. */
const cleanPlateInHand: TutorialSetup = (state) => {
  const held = state.players[SANDBOX_PLAYER_ID]?.holding ?? null
  const next = readyToStart(state)
  plateInHand(next, held, [])
  return next
}


/** The steps for this config and this kitchen, in the order they are read. */
export function stepsFor(config: ClientConfig | null, mapName: string | null): TutorialStep[] {
  const kitchen = kitchenFor(config, mapName)
  const { dishes, items } = kitchen

  const exemplar = items[0] ?? null
  const source = exemplar?.source ?? null
  const sourceLabel = source ? labelOf(config, source) : 'an ingredient'
  const chopped = choppedFrom(config, exemplar)
  const vessel = exemplar?.container ?? kitchen.containers[0] ?? null
  const vesselWord = vessel ? vesselName(vessel) : 'pan'
  const cookStations = kitchen.stations.filter(isCookingEquipment)
  const prepStations = kitchen.stations.filter((s) => !isCookingEquipment(s))
  const leadDish = dishes[0] ?? null
  // The longest dish this kitchen can order, when there is one worth the name.
  // A pan holds a single ingredient, so "more than one item" is exactly the
  // condition under which a plate has to be assembled rather than filled — and
  // a kitchen whose every dish is one item has no assembly lesson to teach.
  const assembly = dishes.reduce<DishSummary | null>(
    (longest, dish) =>
      dish.contents.length > 1 && dish.contents.length > (longest?.contents.length ?? 1) ? dish : longest,
    null,
  )
  /** The first thing to land on a plate here — one item, whatever the dish is. */
  const firstItem = (assembly ?? leadDish)?.contents.slice(0, 1) ?? []
  const hobWord = cookStations[0] ? stationWord(cookStations[0]) : 'stove'

  const { panOnHeat, sourceInHand, choppedOnBoard, cookingInPan, burntPanOnHeat } = progressionSetupsFor(
    source,
    chopped,
    cookStations[0] ?? 'stove',
  )

  const steps: TutorialStep[] = []

  steps.push({
    id: 'move',
    title: 'Move',
    body:
      "Walk with the arrow keys or WASD, but not through counters or walls. Walk into something to face it, and the marker in front of you shows what you'll act on.",
    instruction: 'Press an arrow key or W, A, S, D to take a step.',
    keys: ['←', '↑', '↓', '→'],
    focus: { kind: 'kitchen' },
    draw: [{ kind: 'station', station: 'counter' }, ...prepStations.slice(0, 1).map(stationSpec)],
    animated: false,
    goal: walked,
  })

  steps.push({
    id: 'interact',
    title: `Put the ${vesselWord} on the ${hobWord}`,
    body: 'Tap Space to pick up, put down or serve. Hold it to chop, wash or put out a fire.',
    instruction: `Press Space at the counter to pick up the ${vesselWord}, then Space at the ${hobWord}.`,
    keys: ['Space'],
    focus: { kind: 'stations', stations: ['counter', ...cookStations] },
    draw: [
      { kind: 'container', container: vessel ?? 'pan' },
      ...cookStations.slice(0, 1).map((station): DrawSpec => ({ station, kind: 'station', container: vessel ?? 'pan' })),
    ],
    animated: false,
    goal: putPanOnHeat,
  })

  if (source) {
    steps.push({
      id: 'crate',
      title: 'Grab from a crate',
      body: 'Crates never run out. You can only carry one thing at a time.',
      instruction: `Face the ${sourceLabel.toLowerCase()} crate and press Space.`,
      keys: ['Space'],
      focus: { kind: 'stations', stations: ['crate'] },
      draw: [
        { kind: 'station', station: 'crate', dispenses: source },
        { kind: 'ingredient', id: source },
      ],
      animated: false,
      goal: tookAnIngredient,
      setup: vessel && cookStations.length > 0 ? panOnHeat : undefined,
    })
  }

  if (source && prepStations.includes('board')) {
    steps.push({
      id: 'board',
      title: 'Chop',
      body: 'Most food needs chopping before it can cook. Hold Space to chop, and if you let go, the bar waits for you.',
      instruction: 'Press Space to put it on the board, then hold Space until the bar fills.',
      keys: ['Space'],
      focus: { kind: 'stations', stations: ['board'] },
      draw: [
        { kind: 'ingredient', id: source },
        { kind: 'station', station: 'board' },
        ...(chopped ? [{ kind: 'ingredient', id: chopped } as DrawSpec] : []),
      ],
      animated: false,
      goal: choppedSomething,
      setup: vessel && cookStations.length > 0 ? sourceInHand : undefined,
    })
  }

  if (vessel && cookStations.length > 0) {
    steps.push({
      id: 'hob',
      title: `Cook in a ${vesselWord}`,
      body: `Food goes in the ${vesselWord}, never straight onto the ${hobWord}. A ${vesselWord} cooks one thing at a time.`,
      instruction: `With the food in hand, press Space at the ${hobWord} to add it to the ${vesselWord}.`,
      keys: ['Space'],
      // Keep the chopping board lit with the stove: this is the handoff from
      // chopped meat to the pan, not a new, disconnected station lesson.
      focus: { kind: 'stations', stations: ['board', ...cookStations] },
      draw: [
        { kind: 'container', container: vessel },
        ...cookStations.map(
          (station): DrawSpec => ({
            kind: 'station',
            station,
            container: vessel,
            contents: chopped ? [chopped] : [],
            cookState: 'cooking',
          }),
        ),
      ],
      animated: true,
      goal: startedCooking,
      setup: chopped ? choppedOnBoard : undefined,
    })
  }

  if (vessel && cookStations.length > 0) {
    const hob = cookStations[0]
    steps.push({
      id: 'clocks',
      title: "Don't let it burn",
      body:
        `When the bar is full, the food is done. Take the ${vesselWord} off the heat or plate the food soon after, or it burns.`,
      instruction: `When the bar reaches the mark, press Space to take the ${vesselWord} off the heat.`,
      keys: ['Space'],
      // The bin arrives with the sentence that first needs it: burnt food has
      // exactly one destination, and this is the step that says so.
      focus: { kind: 'stations', stations: [...cookStations, 'bin'] },
      reveals: ['bin'],
      draw: (['cooking', 'done', 'burnt'] as CookState[]).map(
        (cookState): DrawSpec => ({
          kind: 'station',
          station: hob,
          container: vessel,
          contents: chopped ? [chopped] : [],
          cookState,
        }),
      ),
      animated: true,
      goal: cookedThrough,
      setup: chopped ? cookingInPan : undefined,
    })
  }

  steps.push({
    id: 'bin',
    title: 'Bin mistakes',
    body:
      `Burnt food can't be served, so tip it in the bin and keep the ${vesselWord} or plate. Empty plates and the extinguisher never go in the bin.`,
    instruction: `Press Space to pick up the burnt ${vesselWord}, then Space at the bin to empty it.`,
    keys: ['Space'],
    focus: { kind: 'stations', stations: [...cookStations, 'bin'] },
    // `clocks` already brought it in with the sentence that first needed it.
    // Named again here so a kitchen with nothing to cook — and therefore no
    // `clocks` card — still has a bin by the time it is asked for one.
    reveals: ['bin'],
    draw: [
      { kind: 'station', station: 'bin' },
      ...(source ? [{ kind: 'ingredient', id: source } as DrawSpec] : []),
      { kind: 'plate', contents: [] },
    ],
    animated: false,
    goal: threwSomethingAway,
    setup: chopped ? burntPanOnHeat : undefined,
  })

  steps.push({
    id: 'fire',
    title: 'Put out fires',
    body:
      'A station on fire stops working until the fire is out. Take the extinguisher from its rack, hold Space at the fire, then put it back.',
    instruction: 'Take the extinguisher, hold Space at the fire, then put it back on the rack.',
    keys: ['Space'],
    focus: { kind: 'stations', stations: [...cookStations, 'extinguisher_rack'] },
    reveals: ['extinguisher_rack'],
    draw: [
      {
        kind: 'station',
        station: cookStations[0] ?? 'stove',
        container: vessel ?? undefined,
        cookState: vessel ? 'burnt' : undefined,
        onFire: true,
      },
      { kind: 'station', station: 'extinguisher_rack' },
      { kind: 'equipment', id: 'extinguisher' },
    ],
    animated: true,
    goal: rackedTheExtinguisher,
    setup: lightTheStove,
  })

  steps.push({
    id: 'plate',
    title: 'Plate up',
    body: `Take a clean plate to the ${vesselWord} and press Space. The food moves onto the plate, and the ${vesselWord} is free again.`,
    instruction: `Press Space at the plate stack, then at the ${vesselWord}.`,
    keys: ['Space'],
    focus: { kind: 'stations', stations: ['plate_stack', ...cookStations] },
    draw: [
      { kind: 'station', station: 'plate_stack' },
      // One item, because the plate leaves this step with one item on it. On a
      // kitchen whose dishes are single items that is also the finished plate.
      { kind: 'plate', contents: firstItem },
      ...cookStations.slice(0, 1).map(
        (station): DrawSpec => ({
          kind: 'station',
          station,
          container: vessel ?? undefined,
          contents: firstItem,
          cookState: vessel ? 'done' : undefined,
        }),
      ),
    ],
    animated: false,
    goal: platedUp,
    setup: foodReadyToPlate,
  })

  // Only where there is a dish to assemble. On a one-item kitchen — the 5x5,
  // the solo map — the plate is finished the moment it is filled, and a card
  // explaining assembly there would be teaching a step the player never takes.
  if (assembly) {
    steps.push({
      id: 'assemble',
      title: 'Add a second item',
      body: `${assembly.label} needs more than one item, so you build it on the plate. Cook the next item, then bring the same plate back to the ${vesselWord}.`,
      instruction: `With the plate in hand, press Space at the ${hobWord} to add the second item.`,
      keys: ['Space'],
      focus: { kind: 'stations', stations: [...cookStations, 'plate_stack'] },
      // Half a plate, the next thing on the heat, and the plate that comes of
      // putting them together — the three frames of the lesson, in order.
      draw: [
        { kind: 'plate', contents: firstItem },
        ...cookStations.slice(0, 1).map(
          (station): DrawSpec => ({
            kind: 'station',
            station,
            container: vessel ?? undefined,
            contents: [assembly.contents[1]],
            cookState: vessel ? 'done' : undefined,
          }),
        ),
        { kind: 'plate', contents: [...assembly.contents] },
      ],
      animated: false,
      goal: assembledADish,
      setup: halfADish,
    })
  }

  steps.push({
    id: 'serve',
    title: 'Serve',
    body:
      'Take finished dishes to the hatch. Only dishes on an order score points, and the faster you serve, the more they are worth.',
    instruction: 'Carry the finished plate to the hatch and press Space.',
    keys: ['Space'],
    focus: { kind: 'stations', stations: ['hatch'] },
    draw: [
      { kind: 'plate', contents: leadDish ? [...leadDish.contents] : [] },
      { kind: 'station', station: 'hatch' },
    ],
    animated: false,
    goal: served,
    setup: dishReadyToServe,
  })

  steps.push({
    id: 'wash',
    title: 'Wash dirty plates',
    body:
      'Served plates come back dirty at the plate return. There are only a few, so hold Space at the sink to wash them before you run out.',
    instruction: 'Pick up the dirty plate at the plate return, then hold Space at the sink.',
    keys: ['Space'],
    focus: { kind: 'stations', stations: ['plate_return', 'sink'] },
    draw: [
      { kind: 'plate', dirty: true },
      { kind: 'station', station: 'sink' },
      { kind: 'station', station: 'plate_return' },
    ],
    animated: true,
    goal: washed,
    setup: dirtyPlateWaiting,
  })

  steps.push({
    id: 'stack',
    title: 'Put clean plates back',
    body:
      "Don't carry a clean plate you don't need yet. The plate stack takes it back, so your hands are free.",
    instruction: 'Face the plate stack with the clean plate and press Space.',
    keys: ['Space'],
    focus: { kind: 'stations', stations: ['plate_stack', 'plate_return'] },
    draw: [
      { kind: 'plate', contents: [] },
      { kind: 'station', station: 'plate_stack' },
      { kind: 'station', station: 'plate_return' },
    ],
    animated: false,
    goal: stackedAPlate,
    setup: cleanPlateInHand,
  })

  steps.push({
    id: 'ticket',
    title: 'Cook one more order',
    body:
      'Orders line up on the left, most urgent first, and each bar shows the time left. Press R to see every recipe while the clock keeps running.',
    instruction: `Make and serve another ${leadDish?.label ?? 'dish'} to finish the tutorial.`,
    keys: ['Space', 'R'],
    // The capstone is the full room: the recipe, the counter, and both crates
    // must all be usable to cook the combination from scratch.
    focus: { kind: 'kitchen' },
    reveals: [...HIDEABLE_STATIONS],
    unlocks: ['carrot'],
    fresh: true,
    draw: [{ kind: 'plate', contents: leadDish ? [...leadDish.contents] : [] }],
    animated: false,
    dish: leadDish?.id,
    goal: leadDish ? servedDish(leadDish.contents) : served,
  })

  steps.push({
    id: 'complete',
    title: 'Tutorial complete',
    body:
      `You cooked a full dish and served it. Keep an eye on the orders, keep the ${vesselWord} moving, and use the counter to pass things to your partner.`,
    instruction: 'Choose below when you are ready to play.',
    keys: ['Enter'],
    focus: { kind: 'kitchen' },
    // Keep the completion card self-contained for readers who page straight
    // here, as well as for players who arrived by finishing the capstone.
    reveals: [...HIDEABLE_STATIONS],
    draw: [{ kind: 'plate', contents: leadDish ? [...leadDish.contents] : [] }],
    animated: false,
  })

  return steps
}

// ---------------------------------------------------------------------------
// What this kitchen actually contains
// ---------------------------------------------------------------------------

interface Kitchen {
  map: MapSummary | null
  dishes: DishSummary[]
  items: RequiredItem[]
  stations: StationType[]
  containers: ContainerKind[]
}

/**
 * The dishes this map can order, and everything they imply.
 *
 * An unknown map name falls back to the default and then to whatever the server
 * listed first, because a tutorial that renders nothing is worse than one that
 * teaches the default kitchen. `requires.stations` excludes crates and the
 * hatch by construction on the server, so those two are taught unconditionally
 * by their own steps rather than derived here.
 */
function kitchenFor(config: ClientConfig | null, mapName: string | null): Kitchen {
  const maps = config?.maps ?? []
  const map =
    maps.find((m) => m.name === mapName) ??
    maps.find((m) => m.name === config?.defaultMap) ??
    maps[0] ??
    null

  const pool = map?.orderPool ?? []
  const all = config?.dishes ?? []
  const dishes = pool.length > 0 ? all.filter((dish) => pool.includes(dish.id)) : all

  const items = dishes.flatMap((dish) => dish.requires.items)
  const stations: StationType[] = []
  const containers: ContainerKind[] = []
  for (const dish of dishes) {
    for (const station of dish.requires.stations) {
      if (!stations.includes(station)) stations.push(station)
    }
    for (const container of dish.requires.containers) {
      if (!containers.includes(container)) containers.push(container)
    }
  }

  return { map, dishes, items, stations, containers }
}

const stationSpec = (station: StationType): DrawSpec => ({ kind: 'station', station })

/** The one-hop intermediate between a crate ingredient and its cooked form. */
function choppedFrom(config: ClientConfig | null, item: RequiredItem | null): IngredientId | null {
  if (!item || item.stations.length === 0) return null
  const first = item.stations[0]
  const found = (config?.ingredients ?? []).find(
    (ingredient: IngredientSummary) =>
      ingredient.from?.station === first && ingredient.from.ingredient === item.source,
  )
  return found?.id ?? null
}

function labelOf(config: ClientConfig | null, id: IngredientId): string {
  return (config?.ingredients ?? []).find((ingredient) => ingredient.id === id)?.label ?? id
}

/** A container kind, as it reads in a sentence: `pan` stays a pan, and an id with underscores loses them. */
const vesselName = (kind: ContainerKind): string => String(kind).replace(/_/g, ' ')

/** The same, for a station: `plate_return` becomes `plate return`. */
const stationWord = (station: StationType): string => String(station).replace(/_/g, ' ')
